/* facets.js — Sneak Freak (AphCom) · <sf-facets>
   Filters / sort / load-more for the collection and search pages via the Section Rendering API.
   Markup contract (sections/main-collection.liquid, main-search.liquid, snippets/facets*.liquid, pagination.liquid):
   [data-facets-form] GET form (inputs may live outside it and carry form="…"), [data-facets-sort], [data-facets-price],
   [data-facets-results], [data-facets-active], [data-facets-count-wrap], [data-facets-badge], [data-facets-groups="scope"],
   [data-facets-clear], [data-facets-apply], [data-load-more], [data-pagination], [data-plp-sticky],
   [data-sort-value], [data-plp-tabs] / [data-plp-tab] (real links; sort_by is carried, and data-keep — the store's own
   in-stock filter when sold-out products are hidden — but never the shopper's filter.*).
   URL state is pushed to history; popstate re-renders. Works without JS (the form submits normally).
   Filter groups are native <details>: this only adds the height animation, carries which groups were open
   (and how far the sidebar/popup was scrolled) across a results re-render, and never touches what is checked —
   selections live in the form and come back from the server URL.

   Infinite loading ([data-pagination][data-infinite], snippets/pagination.liquid):
   - An IntersectionObserver watches the pagination nav with an 800px bottom margin. One page at a time: a load
     never starts while another is in flight, and the nav is re-checked after each page, so a screen that is
     still short of the margin keeps going one page after another instead of stalling — and nothing is ever
     fetched ahead of the visitor, so the footer is reachable as soon as the list ends. The real "Load more"
     link (?page=N+1, every filter and the sort kept) is the no-JS path, the no-IntersectionObserver path, the
     "Try again" after an error, the keyboard path, and — visible whenever nothing is loading — the way on if the
     automatic trigger cannot run. A card whose product is already in the grid is never appended again.
   - Each page is fetched through the Section Rendering API and its cards are appended to the grid. Every card
     behaviour is a custom element (colours, favourite, quick add, sizes), so appended cards upgrade exactly
     like server-rendered ones; theme.initReveal / initTitleMasks are re-run on the grid for the reveal.
   - ?page= follows along through history.replaceState — no new history entries.
   - Back / reload: the cards of every page in the list are kept in sessionStorage (one entry per list, keyed by
     the URL without ?page=, a few lists at most), and the first visible card + its offset are saved on
     pagehide. Arriving back on ?page=N, the server has rendered page N alone; the cached pages before it are
     put back in front of it synchronously — any page missing from the cache (quota, or no entry at all) is
     fetched, but only for a list the loader built in that history entry; a ?page=N opened directly comes back
     as page N alone — and the visitor is returned to the same card at the same offset. history.scrollRestoration is
     set to manual on an entry once it has appended pages, so the browser's own restore cannot fight this one. A bfcache
     restore needs none of it and is left alone. Filter and sort changes start a fresh list at page 1. */
'use strict';
(function () {
  const theme = window.theme || {};
  if (customElements.get('sf-facets')) return;
  const SWAP = ['[data-facets-results]', '[data-facets-active]', '[data-facets-count-wrap]', '[data-facets-badge]', '[data-facets-title]'];
  const NEAR_PX = 800; /* how far below the viewport the end of the grid starts the next page */
  const ROOT_MARGIN = '0px 0px ' + NEAR_PX + 'px 0px';
  const LIST_KEY = 'sf:plp:list:';
  const POS_KEY = 'sf:plp:pos:';
  const MAX_LISTS = 4;

  /* the list a URL shows, whatever page it is on: path + query without page / section_id */
  const listId = (href) => {
    const u = new URL(href, window.location.origin);
    u.searchParams.delete('page');
    u.searchParams.delete('section_id');
    return u.pathname + u.search;
  };
  const clean = (href) => {
    const u = new URL(href, window.location.origin);
    u.searchParams.delete('section_id');
    return u.pathname + u.search + u.hash;
  };
  const store = {
    get(k) { try { return JSON.parse(window.sessionStorage.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { window.sessionStorage.setItem(k, JSON.stringify(v)); return true; } catch (e) { return false; } },
    del(k) { try { window.sessionStorage.removeItem(k); } catch (e) { /* private mode */ } },
    keys(prefix) {
      const out = [];
      try { for (let i = 0; i < window.sessionStorage.length; i++) { const k = window.sessionStorage.key(i); if (k && k.indexOf(prefix) === 0) out.push(k); } } catch (e) { /* private mode */ }
      return out;
    }
  };

  customElements.define('sf-facets', class extends HTMLElement {
    connectedCallback() {
      this._ctrl = new AbortController();
      const { signal } = this._ctrl;
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facets-form]');
      this._reqId = 0;
      this._gen = 0; // bumped whenever the results are re-rendered; a page fetched for an older list is dropped
      // any control bound to the form (inside or via form="…")
      document.addEventListener('change', (e) => this._onChange(e), { signal });
      document.addEventListener('input', (e) => this._onInput(e), { signal });
      this.addEventListener('click', (e) => this._onClick(e), { signal });
      this.addEventListener('click', (e) => this._onSummary(e), { signal });
      if (this.form) this.form.addEventListener('submit', (e) => { e.preventDefault(); this.render(this._urlFromForm(), true); this._closeDrawer(); }, { signal });
      window.addEventListener('popstate', () => this.render(window.location.href, false), { signal });
      /* Each part starts on its own: one that throws must not leave the others unwired (the tabs ran before the
         infinite loader, so an error there used to leave the grid with no automatic loading at all). */
      [this._sticky, this._syncSortValue, this._tabs, this._infinite].forEach((init) => {
        try { init.call(this); } catch (err) { if (window.console) console.error('sf-facets', err); }
      });
    }
    disconnectedCallback() {
      this._ctrl.abort();
      if (this._abort) this._abort.abort();
      this._cancelMore();
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      if (this._tabsRO) this._tabsRO.disconnect();
      if (this._unsubHeader) this._unsubHeader();
    }
    _controls() { return Array.from(document.querySelectorAll('[form="' + (this.form && this.form.id) + '"], #' + (this.form && this.form.id) + ' input, #' + (this.form && this.form.id) + ' select')); }
    _owns(el) { return !!(this.form && el && (this.form.contains(el) || el.getAttribute('form') === this.form.id)); }
    /* The sidebar and the popup are BOTH in the DOM at every width (the sidebar is only display:none on
       phones, the popup only hidden), and both render the same named inputs into #facets-form. _urlFromForm
       unions every checked control, so an unchecked box could never cancel its still-checked twin: a filter
       could be added but never removed by clicking it again. Mirroring the change onto the twin first makes
       the two copies agree, so the union is correct and the two entry points stay visually in sync. */
    _mirror(t) {
      if (!t.name) return;
      const price = t.matches('[data-facets-price]');
      this._controls().forEach((el) => {
        if (el === t || el.name !== t.name) return;
        if (price) { if (el.matches('[data-facets-price]')) el.value = t.value; return; }
        if ((el.type === 'checkbox' || el.type === 'radio') && el.value === t.value) el.checked = t.checked;
      });
    }
    _onChange(e) {
      const t = e.target;
      if (!this._owns(t)) return;
      if (t.matches('[data-facets-price]')) return; // handled on input (debounced)
      if (t.matches('[data-facets-sort]')) this._syncSortValue(t);
      this._mirror(t);
      this.render(this._urlFromForm(), true);
    }
    /* The visible "Sort: value" text over the transparent native select (snippets/plp-toolbar.liquid). */
    _syncSortValue(sel) {
      sel = sel || this.querySelector('[data-facets-sort]');
      if (!sel) return;
      const box = sel.closest('.plp-sort');
      const out = box && box.querySelector('[data-sort-value]');
      const opt = sel.options[sel.selectedIndex];
      if (out && opt) out.textContent = opt.textContent.trim();
    }
    _onInput(e) {
      const t = e.target;
      if (!this._owns(t) || !t.matches('[data-facets-price]')) return;
      this._mirror(t);
      clearTimeout(this._priceTimer);
      this._priceTimer = setTimeout(() => this.render(this._urlFromForm(), true), 500);
    }
    _onClick(e) {
      /* A preset's href is a correct no-JS fallback, but rebuilding from the form is better still: it cannot
         drop a filter the merchant added after the link was rendered. Write the range into the price inputs
         (both copies) and re-render from the form. */
      const preset = e.target.closest('[data-price-preset]');
      if (preset) {
        const scope = preset.closest('[data-facets-groups]') || this;
        /* Suffix match only. A "contains" fallback is a trap here: every Shopify filter param starts with
           "filter", and the word filter itself contains "lte" (fi-lte-r), so [name*="lte"] matched the gte
           input as well and the max assignment overwrote the min that had just been set. */
        const min = scope.querySelector('[data-facets-price][name$=".gte"]');
        const max = scope.querySelector('[data-facets-price][name$=".lte"]');
        if (min || max) {
          e.preventDefault();
          if (min) { min.value = preset.dataset.gte || ''; this._mirror(min); }
          if (max) { max.value = preset.dataset.lte || ''; this._mirror(max); }
          clearTimeout(this._priceTimer);
          this.render(this._urlFromForm(), true);
          return;
        }
      }
      const remove = e.target.closest('[data-facets-remove], [data-facets-clear]');
      if (remove && remove.href) { e.preventDefault(); this._refocus(remove); this.render(remove.href, true); return; }
      const more = e.target.closest('[data-load-more]');
      if (more && more.href) { e.preventDefault(); this.loadMore(more); }
    }
    _urlFromForm() {
      const url = new URL(this.dataset.url || window.location.pathname, window.location.origin);
      const params = new URLSearchParams();
      const seen = new Set();
      this._controls().forEach((el) => {
        if (!el.name || el.disabled) return;
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        if (el.value === '' && el.type !== 'checkbox') return;
        const k = el.name + '=' + el.value; if (seen.has(k)) return; seen.add(k); // aside + drawer duplicates
        params.append(el.name, el.value);
      });
      url.search = params.toString();
      return url.toString();
    }
    async render(href, push) {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('page');
      const fetchUrl = new URL(url); fetchUrl.searchParams.set('section_id', this.sectionId);
      const reqId = ++this._reqId;
      if (this._abort) this._abort.abort();
      this._abort = new AbortController();
      /* a page still on its way belongs to the old list: never let it land in the new grid */
      this._cancelMore();
      this._gen++;
      this.classList.add('is-loading');
      const results = this.querySelector('[data-facets-results]'); if (results) results.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(fetchUrl, { signal: this._abort.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error('facets ' + res.status);
        const html = await res.text();
        if (reqId !== this._reqId) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const focused = document.activeElement && document.activeElement.id;
        const groupState = this._captureGroups();
        /* the pristine cards of the new page 1, for the back/reload cache, before anything upgrades them */
        const newGrid = doc.querySelector('[data-facets-grid]');
        this._list = { start: 1, last: 1, pages: { 1: newGrid ? newGrid.innerHTML : '' } };
        SWAP.forEach((sel) => {
          const n = doc.querySelector(sel), c = this.querySelector(sel);
          if (n && c) {
            /* The result count is an aria-live region. Replacing the node removes the region and adds a new
               one, which most screen readers do not announce; writing into the existing node does. */
            if (sel === '[data-facets-count-wrap]') { if (c.innerHTML !== n.innerHTML) c.innerHTML = n.innerHTML; }
            else c.replaceWith(n);
          } else if (c && !n && sel === '[data-facets-badge]') { c.hidden = true; }
        });
        // facet groups: swap per scope so drawer/aside stay in sync but keep open state
        this.querySelectorAll('[data-facets-groups]').forEach((cur) => {
          const scope = cur.getAttribute('data-facets-groups');
          const next = doc.querySelector('[data-facets-groups="' + scope + '"]');
          if (next) cur.replaceWith(next);
        });
        this._restoreGroups(groupState);
        /* any animation was attached to nodes that no longer exist */
        this.querySelectorAll('.facet.is-animating').forEach((d) => {
          d.classList.remove('is-animating');
          const p = d.querySelector('.facet__panel');
          if (p) p.style.removeProperty('--rows');
        });
        const sort = doc.querySelector('[data-facets-sort]'), curSort = this.querySelector('[data-facets-sort]');
        if (sort && curSort) { curSort.value = sort.value; this._syncSortValue(curSort); }
        if (focused) { const again = document.getElementById(focused); if (again) again.focus({ preventScroll: true }); }
        this._applyRefocus();
        if (push && window.history && window.history.pushState) window.history.pushState({ facets: true }, '', url.toString());
        /* a Back/Forward re-render shows page 1, so the address must stop claiming ?page=N */
        else if (!push && window.history && window.history.replaceState && url.toString() !== window.location.href) window.history.replaceState({ facets: true }, '', url.toString());
        this._syncTabs(url.toString());
        this._armMore();
        this.dispatchEvent(new CustomEvent('facets:change', { bubbles: true, detail: { url: url.toString() } }));
        if (theme.initReveal) theme.initReveal(this);
        if (theme.initTitleMasks) theme.initTitleMasks(this);
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (theme.toast) theme.toast((theme.strings && theme.strings.error_generic) || 'Something went wrong. Please try again.', { type: 'error' });
      } finally {
        if (reqId === this._reqId) { this.classList.remove('is-loading'); const r = this.querySelector('[data-facets-results]'); if (r) r.setAttribute('aria-busy', 'false'); }
      }
    }
    /* ---------------------------------------------------------------- load more / infinite loading */
    _nav() { return this.querySelector('[data-facets-results] [data-pagination]') || this.querySelector('[data-pagination]'); }
    _grid() { return this.querySelector('[data-facets-grid]'); }
    _note(nav, text) {
      const n = nav && nav.querySelector('[data-pagination-note]');
      if (n && text) n.textContent = text;
    }
    _linkLabel(link, text) {
      const l = link && (link.querySelector('.btn__label') || link);
      if (l && text) l.textContent = text;
    }
    _cancelMore() {
      if (this._moreAbort) this._moreAbort.abort();
      this._moreAbort = null;
      this._moreBusy = false;
      if (this._moreIO) { this._moreIO.disconnect(); this._moreIO = null; }
    }
    _infinite() {
      const grid = this._grid(), nav = this._nav();
      const page = nav ? (parseInt(nav.dataset.page, 10) || 1) : 1;
      /* the server-rendered cards, captured before anything (reveal, colour strips) touches them */
      this._list = { start: page, last: page, pages: {} };
      this._list.pages[page] = grid ? grid.innerHTML : '';
      if (nav && nav.hasAttribute('data-infinite')) {
        const { signal } = this._ctrl;
        const save = () => this._savePos();
        window.addEventListener('pagehide', save, { signal });
        document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') save(); }, { signal });
      }
      const restoring = this._restore();
      if (restoring && typeof restoring.then === 'function') restoring.then(() => this._armMore(), () => this._armMore());
      else this._armMore();
    }
    /* (Re)attach the observer to the current nav. Called after every page, so the initial callback that
       observe() always delivers re-checks a nav that is still inside the margin. With `chain` (right after an
       automatic page landed) a nav that is still inside the margin starts the next page in the same task, so the
       Load more link — visible while idle — never flashes for a frame between two pages. Nothing is ever loaded
       unless the end of the grid is within the margin: the footer is reachable as soon as the list ends. */
    _near(nav) {
      const r = nav.getBoundingClientRect();
      return r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight) + NEAR_PX;
    }
    _armMore(chain) {
      if (this._moreIO) { this._moreIO.disconnect(); this._moreIO = null; }
      const nav = this._nav();
      if (!nav || !nav.hasAttribute('data-infinite')) return;
      const link = nav.querySelector('[data-load-more]');
      if (!link || nav.classList.contains('is-error') || this._restoring || typeof window.IntersectionObserver !== 'function') {
        nav.classList.remove('is-auto');
        return;
      }
      nav.classList.add('is-auto');
      if (chain && !this._moreBusy && this._near(nav)) { this.loadMore(link, { auto: true }); return; }
      this._moreIO = new IntersectionObserver((entries) => {
        if (!entries.some((en) => en.isIntersecting) || this._moreBusy) return;
        const next = nav.querySelector('[data-load-more]');
        if (next) this.loadMore(next, { auto: true });
      }, { rootMargin: ROOT_MARGIN });
      this._moreIO.observe(nav);
    }
    async loadMore(link, opts) {
      const auto = !!(opts && opts.auto);
      if (this._moreBusy) return;
      const nav = link.closest('[data-pagination]');
      const infinite = !!(nav && nav.hasAttribute('data-infinite'));
      const href = clean(link.href);
      const fetchUrl = new URL(href, window.location.origin); fetchUrl.searchParams.set('section_id', this.sectionId);
      const gen = this._gen;
      this._moreBusy = true;
      this._moreAbort = new AbortController();
      const signal = this._moreAbort.signal;
      let failed = false;
      link.classList.add('is-loading'); link.setAttribute('aria-busy', 'true');
      if (infinite) { nav.classList.add('is-loading'); this._note(nav, nav.dataset.strLoading); }
      try {
        const res = await fetch(fetchUrl, { signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error('page ' + res.status);
        const html = await res.text();
        if (gen !== this._gen) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('[data-facets-grid]'), grid = this._grid();
        const newPag = doc.querySelector('[data-pagination]');
        let first = null;
        /* a page past the end renders the empty state inside the grid wrapper — never append that */
        if (newGrid && grid && !newGrid.classList.contains('plp-grid--empty')) {
          /* Never the same product twice. Offset pages over a sort with ties (or a catalogue that changed
             between two requests) can repeat a card across a page boundary. */
          const seen = new Set();
          Array.from(grid.children).forEach((c) => { if (c.dataset && c.dataset.handle) seen.add(c.dataset.handle); });
          const cards = Array.from(newGrid.children).filter((c) => {
            const h = c.dataset && c.dataset.handle;
            if (!h) return true;
            if (seen.has(h)) return false;
            seen.add(h);
            return true;
          });
          const pageHtml = cards.map((c) => c.outerHTML).join('');
          first = cards[0] || null;
          const frag = document.createDocumentFragment();
          cards.forEach((card) => frag.appendChild(document.adoptNode(card)));
          grid.appendChild(frag);
          const page = newPag ? (parseInt(newPag.dataset.page, 10) || 0) : 0;
          if (page) this._remember(page, pageHtml, href);
        }
        if (infinite) {
          if (nav.classList.contains('is-error')) { nav.classList.remove('is-error'); this._linkLabel(link, nav.dataset.strMore); }
          this._mergeNav(nav, newPag);
        } else if (nav) {
          if (newPag) {
            newPag.querySelectorAll('a[href]').forEach((a) => a.setAttribute('href', clean(a.getAttribute('href'))));
            nav.replaceWith(newPag);
          } else nav.remove();
        }
        if (window.history && window.history.replaceState) {
          window.history.replaceState({ facets: true }, '', href);
          /* this entry's position is ours to restore now (see the header) */
          try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'; } catch (e) { /* ignore */ }
        }
        if (theme.initReveal) theme.initReveal(grid || this);
        if (theme.initTitleMasks) theme.initTitleMasks(grid || this);
        /* a click (or Enter) on Load more hands focus to the first new product; an automatic load never
           moves focus */
        if (!auto && first) {
          const target = first.querySelector('.card-b__link') || first.querySelector('a[href]:not([tabindex="-1"]), button');
          if (target) target.focus({ preventScroll: true });
        }
        this.dispatchEvent(new CustomEvent('facets:more', { bubbles: true, detail: { url: href } }));
      } catch (err) {
        if (err.name === 'AbortError') return;
        failed = true;
        if (infinite) {
          /* the error line, and the same real link — visible now, reading "Try again" — to retry with */
          nav.classList.add('is-error');
          nav.classList.remove('is-auto');
          if (this._moreIO) { this._moreIO.disconnect(); this._moreIO = null; }
          this._note(nav, nav.dataset.strError);
          this._linkLabel(link, nav.dataset.strRetry);
        } else if (theme.toast) {
          theme.toast((theme.strings && theme.strings.error_generic) || 'Something went wrong. Please try again.', { type: 'error' });
        }
      } finally {
        if (gen === this._gen) {
          this._moreBusy = false;
          link.classList.remove('is-loading'); link.removeAttribute('aria-busy');
          if (nav) nav.classList.remove('is-loading');
          if (infinite && !failed) this._armMore(auto);
        }
      }
    }
    /* Update the nav in place rather than replacing it: its note is the live region, and a replaced region is
       not announced by most screen readers. */
    _mergeNav(cur, next) {
      const link = cur.querySelector('[data-load-more]');
      const nextLink = next && next.querySelector('[data-load-more]');
      if (next) ['page', 'pages', 'total', 'pageSize'].forEach((k) => { if (next.dataset[k]) cur.dataset[k] = next.dataset[k]; });
      if (nextLink && link) link.setAttribute('href', clean(nextLink.getAttribute('href')));
      else if (nextLink) { const a = document.adoptNode(nextLink); a.setAttribute('href', clean(a.getAttribute('href'))); cur.appendChild(a); }
      else if (link) {
        if (link === document.activeElement) { const n = cur.querySelector('[data-pagination-note]'); if (n) { n.setAttribute('tabindex', '-1'); n.focus({ preventScroll: true }); } }
        link.remove();
      }
      cur.classList.toggle('is-end', !nextLink);
      const note = next && next.querySelector('[data-pagination-note]');
      if (note) this._note(cur, note.textContent.trim());
    }

    /* ---------------------------------------------------------------- back / reload restore */
    _remember(page, html, href) {
      const list = this._list;
      if (!list) return;
      list.pages[page] = html;
      list.last = page;
      const id = LIST_KEY + listId(href);
      const value = { start: list.start, last: list.last, pages: list.pages, t: Date.now() };
      /* a handful of lists at most, oldest out first; under a full quota, keep the bookkeeping without the
         markup — a restore then fetches what it is missing */
      const others = store.keys(LIST_KEY).filter((k) => k !== id)
        .map((k) => ({ k, t: (store.get(k) || {}).t || 0 })).sort((a, b) => a.t - b.t);
      while (others.length >= MAX_LISTS) store.del(others.shift().k);
      if (store.set(id, value)) return;
      while (others.length) { store.del(others.shift().k); if (store.set(id, value)) return; }
      store.set(id, { start: list.start, last: list.last, pages: {}, t: value.t });
    }
    _savePos() {
      const grid = this._grid(), nav = this._nav();
      if (!grid || !nav || !nav.hasAttribute('data-infinite')) return;
      const head = this.querySelector('[data-plp-sticky]');
      const line = head ? head.getBoundingClientRect().bottom : 0;
      const kids = grid.children;
      let idx = -1, off = 0;
      for (let i = 0; i < kids.length; i++) {
        const r = kids[i].getBoundingClientRect();
        if (r.bottom > line + 1) { idx = i; off = r.top; break; }
      }
      store.set(POS_KEY + listId(window.location.href), {
        page: parseInt(nav.dataset.page, 10) || 1,
        start: this._list ? this._list.start : 1,
        idx, off, y: window.scrollY,
        pinned: !!(head && head.classList.contains('is-pinned')),
        t: Date.now()
      });
    }
    _navType() {
      try {
        const e = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
        if (e && e.type) return e.type;
        if (performance.navigation) return ({ 1: 'reload', 2: 'back_forward' })[performance.navigation.type] || 'navigate';
      } catch (err) { /* ignore */ }
      return 'navigate';
    }
    /* Returns nothing when there is nothing to do or it finished synchronously, a promise while it fetches. */
    _restore() {
      const nav = this._nav(), grid = this._grid();
      if (!nav || !grid || !nav.hasAttribute('data-infinite')) return null;
      if (window.Shopify && window.Shopify.designMode) return null;
      const type = this._navType();
      if (type !== 'back_forward' && type !== 'reload') return null;
      const id = listId(window.location.href);
      const page = parseInt(nav.dataset.page, 10) || 1;
      let entry = store.get(LIST_KEY + id);
      const pos = store.get(POS_KEY + id);
      const ours = window.history && window.history.scrollRestoration === 'manual';
      const finish = () => {
        this._restoring = false;
        grid.removeAttribute('aria-busy');
        if (ours && pos && pos.page === page) this._restoreScroll(pos);
      };
      if (!entry || entry.last !== page || !(entry.start < page)) {
        /* Nothing usable kept for this list (storage evicted it, or it was never written). Only a list the loader
           built in THIS history entry (`ours`: it set scrollRestoration to manual) is rebuilt, from the page it
           started on, every page before ?page=N fetched. A ?page=N opened directly (shared link, search result,
           the no-JS link) showed page N alone and comes back the same way, at the browser's own scroll position. */
        const start = (pos && pos.page === page && pos.start) || 1;
        if (!ours || start >= page) { finish(); return null; }
        entry = { start, last: page, pages: (entry && entry.start === start && entry.pages) || {} };
      }

      const pages = entry.pages || {};
      const missing = [];
      for (let p = entry.start; p < page; p++) if (!pages[p]) missing.push(p);
      const put = () => {
        let html = '';
        for (let p = entry.start; p < page; p++) html += pages[p] || '';
        grid.insertAdjacentHTML('afterbegin', html);
        /* one card per product, whatever the cached pages held */
        const seen = new Set();
        Array.from(grid.children).forEach((c) => {
          const h = c.dataset && c.dataset.handle;
          if (!h) return;
          if (seen.has(h)) c.remove(); else seen.add(h);
        });
        pages[page] = this._list.pages[page];
        this._list = { start: entry.start, last: page, pages };
        if (entry.start === 1) { const prev = nav.querySelector('[rel="prev"]'); if (prev) prev.remove(); }
        if (theme.initReveal) theme.initReveal(grid);
        if (theme.initTitleMasks) theme.initTitleMasks(grid);
        finish();
      };
      if (!missing.length) { put(); return null; }

      /* Some pages are not in storage: fetch them (a few at a time, each page once) before putting the list back. */
      this._restoring = true;
      grid.setAttribute('aria-busy', 'true');
      const gen = this._gen;
      const base = new URL(window.location.href);
      base.searchParams.set('section_id', this.sectionId);
      const one = (p) => {
        const u = new URL(base); u.searchParams.set('page', p);
        return fetch(u, { headers: { Accept: 'text/html' } }).then((r) => { if (!r.ok) throw new Error('page ' + r.status); return r.text(); })
          .then((h) => { const g = new DOMParser().parseFromString(h, 'text/html').querySelector('[data-facets-grid]'); pages[p] = g && !g.classList.contains('plp-grid--empty') ? g.innerHTML : ''; });
      };
      const batches = [];
      for (let i = 0; i < missing.length; i += 4) batches.push(missing.slice(i, i + 4));
      return batches.reduce((chain, b) => chain.then(() => Promise.all(b.map(one))), Promise.resolve())
        .then(() => { if (gen === this._gen) put(); else finish(); }, () => finish());
    }
    /* Put the same card back at the same offset. The header is pinned first (instantly) when it was pinned
       when the visitor left, so its height is the one the offset was measured against. */
    _restoreScroll(pos) {
      const grid = this._grid();
      const ctl = this._stickyCtl;
      if (ctl) ctl.measureNow();
      if (ctl && pos.pinned) ctl.pinNow();
      const place = () => {
        const card = grid && pos.idx >= 0 ? grid.children[pos.idx] : null;
        const y = card ? window.scrollY + card.getBoundingClientRect().top - pos.off : pos.y;
        /* instant: html.motion-ok sets scroll-behavior: smooth, which would visibly glide down from the top */
        window.scrollTo({ top: Math.max(0, Math.round(y)), left: 0, behavior: 'instant' });
      };
      place();
      /* fonts arriving late can still nudge the layout; correct once more unless the visitor has moved */
      let moved = false;
      const stop = () => { moved = true; };
      const opts = { once: true, passive: true, signal: this._ctrl.signal };
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((t) => window.addEventListener(t, stop, opts));
      const again = () => { if (!moved) place(); };
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => requestAnimationFrame(again)).catch(() => {});
      if (document.readyState !== 'complete') window.addEventListener('load', again, { once: true, signal: this._ctrl.signal });
    }

    /* ---------------------------------------------------------------- collection tabs */
    _tabs() {
      const nav = this.querySelector('[data-plp-tabs]');
      if (!nav) return;
      const { signal } = this._ctrl;
      const fades = () => {
        const max = nav.scrollWidth - nav.clientWidth;
        const pos = Math.abs(nav.scrollLeft); // RTL scrollLeft runs 0 → -max
        nav.classList.toggle('is-fade-start', max > 1 && pos > 1);
        nav.classList.toggle('is-fade-end', max > 1 && pos < max - 1);
      };
      let raf = 0;
      nav.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; fades(); }); }, { passive: true, signal });
      if ('ResizeObserver' in window) { this._tabsRO = new ResizeObserver(fades); this._tabsRO.observe(nav); }
      else window.addEventListener('resize', fades, { passive: true, signal });
      /* The active tab starts in view, and so does any tab reached with the keyboard: Chrome does not scroll a
         focused link that is already partly visible, which left a half-shown tab under the edge fade. */
      this._syncTabs(window.location.href);
      const toActive = () => { const a = nav.querySelector('[data-plp-tab][aria-current="page"]'); if (a) this._tabIntoView(nav, a); };
      toActive();
      /* once more when the web fonts have set the real tab widths, unless the visitor has moved the row */
      let touched = false;
      ['pointerdown', 'wheel', 'touchstart'].forEach((t) => nav.addEventListener(t, () => { touched = true; }, { passive: true, signal, once: true }));
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (!touched) { toActive(); fades(); } }).catch(() => {});
      nav.addEventListener('focusin', (e) => {
        const a = e.target.closest && e.target.closest('[data-plp-tab]');
        if (a) this._tabIntoView(nav, a);
      }, { signal });
      fades();
    }
    /* Aligned to the start edge — the snap position — unless it already shows in full. scrollLeft grows
       towards the physical right in both directions (RTL runs 0 → -max), so one delta serves both. */
    _tabIntoView(nav, tab) {
      if (nav.scrollWidth <= nav.clientWidth + 1) return;
      const cs = getComputedStyle(nav);
      const rtl = cs.direction === 'rtl';
      const edge = parseFloat(cs.scrollPaddingInlineStart || cs.scrollPaddingLeft) || 0;
      const nr = nav.getBoundingClientRect(), r = tab.getBoundingClientRect();
      if (r.left >= nr.left + edge - 1 && r.right <= nr.right - edge + 1) return;
      nav.scrollLeft += rtl ? (r.right - (nr.right - edge)) : (r.left - (nr.left + edge));
    }
    /* Tabs are plain links; this only keeps two things about them current after an in-page filter or sort change
       (and on load, for the same rule the Liquid applies):
       - sort_by is the ONLY parameter carried onto a same-site collection tab (unless the tab's own link sets one).
         filter.* is never carried: a filter is a value of THIS collection's facets, and carried into another
         collection it can match nothing — size 42 EU from shoes left apparel empty. page is never carried.
       - which tab is active: the one whose path is this collection's (locale prefix ignored) and whose own filter.*
         params (and own sort_by, if any) all hold here, the most specific one winning. When no tab has this
         collection's path at all (a menu-driven row), the server's choice is left alone.
       data-href holds the tab's own link as the Liquid rendered it (locale prefix included). */
    _syncTabs(href) {
      const nav = this.querySelector('[data-plp-tabs]');
      if (!nav) return;
      const origin = window.location.origin;
      const cur = new URL(href, origin);
      const sort = cur.searchParams.get('sort_by');
      const pairs = new Set();
      cur.searchParams.forEach((v, k) => { if (k.indexOf('filter.') === 0) pairs.add(k + '=' + v); });
      const root = String((theme.routes && theme.routes.root) || '/').replace(/\/+$/, '');
      const norm = (p) => {
        let out = String(p || '').replace(/\/+$/, '') || '/';
        if (root && (out === root || out.indexOf(root + '/') === 0)) out = out.slice(root.length) || '/';
        return out;
      };
      let here;
      try { here = norm(new URL(this.dataset.url || cur.pathname, origin).pathname); } catch (e) { here = norm(cur.pathname); }
      let best = null, bestScore = -1;
      nav.querySelectorAll('a[data-plp-tab]').forEach((a) => {
        if (!a.hasAttribute('data-href')) a.setAttribute('data-href', a.getAttribute('href') || '');
        let own;
        try { own = new URL(a.getAttribute('data-href'), origin); } catch (e) { return; }
        if (own.origin !== origin) return;
        const go = new URL(own.href);
        if (go.pathname.indexOf('/collections/') > -1 && !own.searchParams.has('sort_by')) {
          if (sort) go.searchParams.set('sort_by', sort); else go.searchParams.delete('sort_by');
        }
        /* the in-stock filter (sections/main-collection.liquid data-keep) goes onto every collection tab */
        if (this.dataset.keep && go.pathname.indexOf('/collections/') > -1 && !go.searchParams.has('view')) {
          const kv = this.dataset.keep.split('=');
          if (kv[0] && !own.searchParams.has(kv[0])) go.searchParams.set(kv[0], kv[1] || '');
        }
        const next = go.pathname + go.search + go.hash;
        if (a.getAttribute('href') !== next) a.setAttribute('href', next);
        if (norm(own.pathname) !== here) return;
        let score = 0;
        own.searchParams.forEach((v, k) => {
          if (score < 0) return;
          if (k.indexOf('filter.') === 0) score = pairs.has(k + '=' + v) ? score + 1 : -1;
          else if (k === 'sort_by') score = sort === v ? score + 1 : -1;
        });
        if (score > bestScore) { bestScore = score; best = a; }
      });
      if (!best) return;
      nav.querySelectorAll('a[data-plp-tab]').forEach((a) => {
        const on = a === best;
        a.classList.toggle('is-active', on);
        if (on) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
      });
    }
    _reduced() { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

    /* A group heading. The browser would open it instantly; we animate the height instead and let the
       browser do it its own way when the visitor has asked for reduced motion. */
    _onSummary(e) {
      const sum = e.target.closest('.facet__summary');
      if (!sum || !this.contains(sum)) return;
      const d = sum.parentElement;
      if (!d || d.tagName !== 'DETAILS' || this._reduced()) return;
      e.preventDefault();
      this._animate(d);
    }
    /* Toggling a group that sits ABOVE what you are looking at changes the height of everything above the
       scroll position, which slides the content you were reading. Holding the offset steady for the whole
       animation keeps the popup and the sidebar still. A group at or below the fold needs no correction —
       nothing above it moved. */
    _anchorScroll(d) {
      const scroller = d.closest('.drawer__body, .plp__aside');
      if (!scroller) return null;
      const dTop = d.getBoundingClientRect().top;
      const sTop = scroller.getBoundingClientRect().top;
      if (dTop >= sTop) return null;
      return { scroller, top: scroller.scrollTop, height: scroller.scrollHeight };
    }
    _holdScroll(d, anchor) {
      if (!anchor) return;
      const tick = () => {
        const delta = anchor.scroller.scrollHeight - anchor.height;
        anchor.scroller.scrollTop = Math.max(0, anchor.top + delta);
        if (d.classList.contains('is-animating')) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
    _animate(d) {
      const panel = d.querySelector('.facet__panel');
      if (!panel) { d.open = !d.open; return; }
      /* Mid-animation, d.open is already true for BOTH directions (a close keeps the panel mounted), so
         !d.open would read a running close as "already open" and close it again. The direction in flight is
         the authority while one is running. */
      const opening = d.classList.contains('is-animating') ? !d._animOpening : !d.open;
      d._animOpening = opening;
      const anchor = this._anchorScroll(d);
      clearTimeout(d._animTimer);
      if (d._animDone) { panel.removeEventListener('transitionend', d._animDone); d._animDone = null; }
      d.classList.add('is-animating');
      this._holdScroll(d, anchor);
      if (opening) d.open = true;
      panel.style.setProperty('--rows', opening ? '0fr' : '1fr');
      /* two frames: the first commits the start value, the second starts the transition */
      requestAnimationFrame(() => requestAnimationFrame(() => {
        panel.style.setProperty('--rows', opening ? '1fr' : '0fr');
      }));
      const done = (ev) => {
        if (ev && ev.propertyName !== 'grid-template-rows') return;
        panel.removeEventListener('transitionend', done);
        d._animDone = null;
        clearTimeout(d._animTimer);
        d.classList.remove('is-animating');
        panel.style.removeProperty('--rows');
        if (!opening) d.open = false;
        if (anchor) {
          const delta = anchor.scroller.scrollHeight - anchor.height;
          anchor.scroller.scrollTop = Math.max(0, anchor.top + delta);
        }
      };
      d._animDone = done;
      panel.addEventListener('transitionend', done);
      d._animTimer = setTimeout(done, 600); // if transitionend never fires, the group must not be left mid-way
    }

    /* Which groups were open, and where the sidebar and the popup were scrolled to. Re-rendering the results
       replaces the whole group list, so without this every group would snap shut and the popup would jump back
       to the top on each tick. */
    _captureGroups() {
      const st = { open: {}, more: {}, aside: null, drawer: null };
      this.querySelectorAll('[data-facets-groups]').forEach((g) => {
        const scope = g.getAttribute('data-facets-groups');
        st.open[scope] = Array.from(g.querySelectorAll('details.facet[open]')).map((d) => d.getAttribute('data-facet-key'));
        /* a value ticked inside an expanded overflow list would otherwise vanish behind a collapsed
           "show more" the moment the results came back */
        st.more[scope] = Array.from(g.querySelectorAll('details.facet')).filter((d) => {
          const m = d.querySelector('.facet__more');
          return m && m.open;
        }).map((d) => d.getAttribute('data-facet-key'));
      });
      const aside = this.querySelector('.plp__aside');
      if (aside) st.aside = aside.scrollTop;
      const body = this.querySelector('#filter-drawer .drawer__body');
      if (body) st.drawer = body.scrollTop;
      return st;
    }
    _restoreGroups(st) {
      this.querySelectorAll('[data-facets-groups]').forEach((g) => {
        const want = st.open[g.getAttribute('data-facets-groups')];
        if (!want) return;
        const wantMore = (st.more && st.more[g.getAttribute('data-facets-groups')]) || [];
        g.querySelectorAll('details.facet').forEach((d) => {
          const key = d.getAttribute('data-facet-key');
          d.open = want.indexOf(key) > -1;
          const m = d.querySelector('.facet__more');
          if (m) m.open = wantMore.indexOf(key) > -1;
        });
      });
      const aside = this.querySelector('.plp__aside');
      if (aside && st.aside != null) aside.scrollTop = st.aside;
      const body = this.querySelector('#filter-drawer .drawer__body');
      if (body && st.drawer != null) body.scrollTop = st.drawer;
    }

    /* The chip the shopper just activated is about to be removed from the DOM, which would drop focus to
       <body> and lose a keyboard user's place. Hand focus to the next chip, or to the result count. */
    _refocus(el) {
      const chips = el.parentElement;
      const next = el.nextElementSibling || el.previousElementSibling;
      this._focusAfter = (next && next.id) ? '#' + next.id : null;
      this._focusFallback = true;
      if (chips) chips.setAttribute('data-refocus', '1');
    }
    _applyRefocus() {
      if (!this._focusFallback) return;
      this._focusFallback = false;
      const chips = this.querySelector('[data-facets-active]');
      const chip = chips && !chips.hidden && chips.querySelector('.plp-chip');
      const target = chip || this.querySelector('[data-facets-count-wrap]');
      if (!target) return;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
    _closeDrawer() { const d = document.getElementById('filter-drawer'); if (d && typeof d.close === 'function') d.close(); }
    /* --plp-sticky-h feeds the filter sidebar's sticky top. Writing it on every frame of the collapse made
       the sidebar re-lay-out ~20 times per transition; it is written once now and once after the motion
       settles, and only when the value actually changed. */
    _syncStickyH(head) {
      clearTimeout(this._hTimer);
      const write = () => {
        const h = Math.round(head.getBoundingClientRect().height);
        if (h === this._lastStickyH) return;
        this._lastStickyH = h;
        this.style.setProperty('--plp-sticky-h', h + 'px');
      };
      write();
      this._hTimer = setTimeout(write, 460);
    }

    /* The collapsing header.
       The old trigger was a single IntersectionObserver threshold. A threshold with no hysteresis flips on
       ANY movement across it, so easing to a stop near the trigger — or a trackpad's last few pixels —
       toggled the header open and shut repeatedly, restarting a 340ms animation each time. Measured: 24
       flips from 24 three-pixel movements.

       Two things fix it. The state is decided from window.scrollY against two thresholds 72px apart, so a
       small wobble cannot cross both. And every measurement is taken from the sentinel, which sits ABOVE the
       header and is measured only while the header is open — so collapsing can never move the thing that
       decides whether to collapse.

       The third defect was the threshold itself. It waited for the WHOLE collapsible block to clear the
       sticky line, so the full-height header stayed parked over the grid for exactly the height of its own
       contents: measured at 1440x900, a banner collection held 492px — 55% of the viewport — for 354px of
       scrolling, with the first product row already sliding underneath it. It gives way after a share of
       that distance now; see TRAVEL_RATIO. */
    _sticky() {
      const head = this.querySelector('[data-plp-sticky]');
      if (!head) return;
      const sentinel = document.createElement('span');
      sentinel.className = 'plp-sticky__sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      head.parentNode.insertBefore(sentinel, head);

      const HYSTERESIS = 72;
      const AT_TOP = 2; // the first couple of pixels, where the header is open whatever the thresholds say
      /* How far the collapsible block travels past the sticky line before the header gives way. A share of
         it, not all of it — all of it is what kept a banner parked over the grid for its own height. The
         cap stops an unusually tall banner from buying itself more time on screen than any other header
         gets: a portrait collection image on a 390px phone measures 516px of collapsible content, and a
         third of that is most of a phone screen. */
      const TRAVEL_RATIO = 0.35;
      const TRAVEL_MAX = 160;
      /* Slack on the canPin test, for the overshoot between the last frame below the threshold and the
         first one above it plus sub-pixel rounding in the two heights it is derived from. */
      const PIN_MARGIN = 16;
      const stickyOffset = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-h')) || 0;
      let pinned = head.classList.contains('is-pinned');
      let pinAt = 0;
      let unpinAt = 0;
      let ticking = false;

      /* Cached, because a reading taken while the header is mid-transition is meaningless: one frame
         after unpinning the collapsible parts are still ~0 tall, which produced a threshold low enough to
         re-pin on the spot. Only a settled, open reading replaces it, and the page's own height is cached
         on the same terms — it is short while the header is collapsed. The sentinel's document position is
         safe to read at any time — it sits above the header, so collapsing cannot move it. */
      let collapsibleH = 0;
      let shrinkH = 0;
      let roomAfterCollapse = Infinity;
      let canPin = true;
      const hasCollapse = !!head.querySelector('.plp-collapse');
      const readCollapsible = () => {
        let d = 0;
        head.querySelectorAll('.plp-collapse').forEach((c) => { d += c.getBoundingClientRect().height; });
        return d;
      };
      /* What the page actually loses when the header pins, which is NOT collapsibleH. Pinning also zeroes
         .plp-head's padding-block-end and closes .plp-hero's — 4 to 6px MORE than the collapsible parts —
         while opening the compact title gives ~26px back. Counting only the collapsible parts made the
         room left over look bigger than it is, which is exactly how the flip loop the guard exists to
         prevent kept firing. So the figure is taken from the header itself: .is-pinned is applied with
         every transition under the block suppressed, the height is read, and the class comes off again
         inside the same task, before the browser can paint. Order matters — .is-pinned is removed while
         transitions are still suppressed, so restoring the open values cannot be seen as a change worth
         animating. */
      const MEASURING = 'plp-sticky--measuring';
      const pinnedHeight = () => {
        const cl = head.classList;
        cl.add(MEASURING, 'is-pinned');
        const h = head.getBoundingClientRect().height;
        cl.remove('is-pinned');
        void head.offsetHeight; /* settle the open values while nothing may animate */
        cl.remove(MEASURING);
        return h;
      };
      const measure = () => {
        const base = sentinel.getBoundingClientRect().top + window.scrollY;
        if (!pinned) {
          const d = readCollapsible();
          /* A header with nothing collapsible at all — the search page — is settled by definition; any
             other one is only settled once its collapsible parts have their height back. */
          if (d > 8 || !hasCollapse) {
            collapsibleH = d;
            shrinkH = Math.max(0, head.getBoundingClientRect().height - pinnedHeight());
          }
          roomAfterCollapse = document.documentElement.scrollHeight - shrinkH - window.innerHeight;
        }
        /* A header with less than HYSTERESIS to give up — a short description, or the search page, which
           has no collapsible parts at all — keeps the old "all of it" rule: there is nothing left to
           protect, and shrinking the travel below the gap would only push unpinAt under the page top. */
        const travel = Math.min(TRAVEL_MAX, Math.max(Math.min(collapsibleH, HYSTERESIS), collapsibleH * TRAVEL_RATIO));
        /* The floor keeps the whole band clear of AT_TOP. A band that reached below it would have the
           top rule doing the un-pinning, so the real distance between the two states would be less than
           HYSTERESIS however far apart the numbers looked. */
        pinAt = Math.max(HYSTERESIS + AT_TOP, Math.round(base - stickyOffset() + travel));
        /* No Math.max(0, …) here: the floor already keeps unpinAt positive, and a clamp on this line would
           quietly narrow the gap instead of keeping it — which is what it did on the search page, where
           collapsibleH is 0, pinAt came out 24 and the gap was a third of what it should have been. */
        unpinAt = pinAt - HYSTERESIS;
        /* Collapsing takes shrinkH out of the page. On a collection short enough that what is left can no
           longer hold a scroll position inside the pinned band, the browser clamps the offset back to the
           top, the top rule re-opens the header, the page grows again and it all repeats: measured 13
           flips and a 93px scroll jump on a four-product collection under a portrait banner. Such a page
           keeps its header open — there is no grid below it worth uncovering anyway.

           The test is against pinAt, not unpinAt. Pinning happens at pinAt, so a page that clears only
           unpinAt passes the guard and then has the browser clamp the offset down by as much as the whole
           hysteresis gap on the very frame the class toggles: measured 72px and 75px jumps. Clearing
           pinAt subsumes the unpinAt test, and the margin absorbs the few pixels between the last frame
           below the threshold and the first one above it. */
        canPin = roomAfterCollapse >= pinAt + PIN_MARGIN;
      };

      const apply = () => {
        ticking = false;
        const y = window.scrollY || window.pageYOffset || 0;
        if (!pinned && y > pinAt && canPin) {
          pinned = true;
          head.classList.add('is-pinned');
          this._syncStickyH(head);
        } else if (pinned && (y < unpinAt || y <= AT_TOP)) {
          pinned = false;
          head.classList.remove('is-pinned');
          this._syncStickyH(head);
          /* re-derive once the header has finished opening, not mid-flight */
          clearTimeout(this._measureTimer);
          this._measureTimer = setTimeout(measure, 420);
        }
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(apply); } };

      const remeasure = () => {
        clearTimeout(this._measureTimer);
        this._measureTimer = setTimeout(() => { measure(); apply(); }, 120);
      };

      measure();
      apply();
      this._syncStickyH(head);

      /* For the back/reload restore (see _restoreScroll): re-derive the thresholds now that the page has grown,
         and pin with every transition suppressed, so the offset the visitor left at is measured against the
         same header height. */
      this._stickyCtl = {
        measureNow: () => { clearTimeout(this._measureTimer); measure(); },
        pinNow: () => {
          if (pinned || !canPin) return;
          head.classList.add('plp-sticky--instant', 'is-pinned');
          pinned = true;
          void head.offsetHeight;
          this._syncStickyH(head);
          requestAnimationFrame(() => requestAnimationFrame(() => head.classList.remove('plp-sticky--instant')));
        }
      };

      const signal = this._ctrl.signal;
      window.addEventListener('scroll', onScroll, { passive: true, signal });
      window.addEventListener('resize', remeasure, { passive: true, signal });
      /* fonts and lazily-decoded images change the header's height after first paint */
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure).catch(() => {});
      window.addEventListener('load', remeasure, { once: true, signal });
      this.addEventListener('facets:change', remeasure, { signal });
      /* a longer page has more room to collapse into (canPin) */
      this.addEventListener('facets:more', remeasure, { signal });
      if (window.theme && theme.pubsub) this._unsubHeader = theme.pubsub.subscribe('header:height', remeasure);
      /* The collection header above the bar (breadcrumb, title, description) is in the page flow: when it changes
         height — Read more / Read less, a late font, the banner image — the bar's line moves with it. */
      const intro = this.querySelector('.plp-intro');
      if (intro && 'ResizeObserver' in window) {
        const ro = new ResizeObserver(remeasure);
        ro.observe(intro);
        signal.addEventListener('abort', () => ro.disconnect());
      }
    }
  });
})();
